import React from 'react';
import { Link } from 'react-router-dom';

const Home = () => {
  const features = [
    {
      icon: '🐕',
      title: 'Pet Management',
      description: 'Add and manage multiple pets. Keep track of all your furry, feathered, or scaled friends in one convenient place.',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: '💚',
      title: 'Health Tracking',
      description: 'Monitor your pet\'s health metrics, vaccinations, medications, and vet appointments. Stay on top of their wellbeing.',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      icon: '🏃',
      title: 'Fitness Monitoring',
      description: 'Track daily exercise, activity levels, and fitness goals. Ensure your pet stays active and healthy.',
      gradient: 'from-orange-500 to-red-500'
    },
    {
      icon: '📅',
      title: 'Activity Routines',
      description: 'Set up daily, weekly, and monthly routines. Track feeding schedules, walks, playtime, and other important activities.',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: '📊',
      title: 'Health Insights',
      description: 'Get detailed progress reports and insights about your pet\'s health trends. Make informed decisions about their care.',
      gradient: 'from-indigo-500 to-blue-500'
    },
    {
      icon: '🩺',
      title: 'Pet Services',
      description: 'Access a comprehensive range of pet-related services and resources to help you provide the best care for your companion.',
      gradient: 'from-teal-500 to-cyan-500'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Add Your Pet',
      description: 'Create a profile for your pet with all their important details and information.'
    },
    {
      number: '02',
      title: 'Track Activities',
      description: 'Log daily activities, health metrics, and routines to build a comprehensive record.'
    },
    {
      number: '03',
      title: 'Get Insights',
      description: 'Review progress reports and insights to understand your pet\'s health trends.'
    },
    {
      number: '04',
      title: 'Take Action',
      description: 'Use the insights to make informed decisions and provide better care for your pet.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/10 via-white to-secondary/5 pt-20 pb-32">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 leading-tight">
                  Track Your Pet's
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary-dark">
                    Health & Activity
                  </span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed max-w-2xl">
                  ACT PAWS is your comprehensive pet care platform. Monitor your pet's health, 
                  fitness, daily routines, and get valuable insights to ensure they live their 
                  happiest, healthiest life.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary inline-block text-center">
                  Get Started
                </Link>
                <a href="#features" className="btn-secondary inline-block text-center">
                  Learn More
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="relative z-10">
                <div className="bg-gradient-to-br from-primary/20 to-primary/10 rounded-3xl p-8 shadow-2xl transform hover:scale-105 transition-transform duration-300">
                  <img 
                    src="/images/pets-group.png" 
                    alt="Group of pets" 
                    className="w-full h-auto object-contain rounded-2xl"
                  />
                </div>
              </div>
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-primary/20 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-4 -left-4 w-40 h-40 bg-secondary/20 rounded-full blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              Why Choose ACT PAWS?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to take care of your beloved pets
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
              >
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center text-3xl mb-6 transform group-hover:scale-110 transition-transform duration-300`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get started in just a few simple steps
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-8 border border-gray-100 hover:border-primary/50 transition-all duration-300 hover:shadow-xl text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-primary to-primary-dark text-white text-2xl font-bold mb-6">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Screenshots Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              See ACT PAWS in Action
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Explore our app interface and discover how easy it is to track your pet's health
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[
              'WhatsApp Image 2025-11-10 at 00.35.21.jpeg',
              'WhatsApp Image 2025-11-10 at 00.35.22.jpeg',
              'WhatsApp Image 2025-11-10 at 00.35.22 (1).jpeg',
              'WhatsApp Image 2025-11-10 at 00.35.22 2.jpeg',
              'WhatsApp Image 2025-11-10 at 00.35.23.jpeg',
              'WhatsApp Image 2025-11-10 at 00.35.23 (1).jpeg',
              'WhatsApp Image 2025-11-10 at 00.35.23 (2).jpeg'
            ].map((image, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-2xl border-2 border-gray-100 hover:border-primary transition-all duration-300 hover:shadow-xl"
              >
                <img
                  src={`/images/app-screenshots/${image}`}
                  alt={`ACT PAWS App Screenshot ${index + 1}`}
                  className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-primary to-primary-dark relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6">
            Ready to Start Tracking?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of pet owners who trust ACT PAWS to keep their pets healthy and happy.
          </p>
          <Link to="/contact" className="inline-block bg-white text-primary hover:bg-gray-50 font-semibold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-2xl">
            Contact Us Today
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
